// ./src/pages/private/EventCouponsPage/CouponGrantRulesDrawer.tsx
//
// The event's VOUCHER SETTINGS (a URL stage, `…/coupons/rules`). It was "RSVP voucher rules" until 2026-10-25 — the
// owner: "it contains expiration settings that are needed for ticketed events too … so it is confusing". So it is named
// for what it holds, in four parts:
//   • when this event's vouchers expire — every voucher, RSVP or ticketed (enforced by `CouponCore.redeem`);
//   • where they are redeemed — a default place, and voucher types redeemed somewhere else (owner, 2026-10-25: "a
//     reference to venue's entrance/service point record so we could set where to redeem a voucher");
//   • the free-text header and footer printed on them;
//   • on an RSVP event only, which vouchers guests get when they reply ("2 drink vouchers per person, 1 photo voucher
//     per group") and whether that happens by itself. A ticketed event gives vouchers per TICKET TYPE instead, so the
//     section says where that is rather than offering rules that would do nothing.
// All of it lives in `event.settings.coupons` (small per-event config, no table).
//
// ⓘ Saved through the event's update, from the event as read FRESH when saving — `settings` is stored whole, so a copy
//   read when the page opened would put back anything another screen changed since (LostFoundSettingsDrawer does the same).
// ⚠ The component keeps its name: the event wizard opens it too (`VouchersStep`, RSVP events, `rsvpFirst`).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton, Stack, Switch, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  CouponGrantRule,
  CouponType,
  CouponTypeService,
  Event,
  EventService,
  EventSettings,
  ServicePoint,
  ServicePointService,
} from "@/utils/api";
import { useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from "@/hooks";
import { AppDrawer, DateTimeField, SelectField, TextField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS, formatErrors, getFormErrors } from "@/utils";
import { COUPON_PRINT_TEXT_MAX, COUPON_SETTINGS_FIELD, couponPlaceSettingsOf } from "@/utils/couponPlaces";
import { servicePointLabel } from "@/utils/servicePoints";
import {
  CouponGrantRulesEditor,
  CouponRedeemPlacesEditor,
  GrantRuleRow,
  RedeemPlaceRow,
} from "@/components/features/Coupons";
import { EVENT_MODE } from "@/components/features/CreateEventWizard/wizardTypes";

export interface CouponGrantRulesDrawerProps {
  opened: boolean;
  onClose: () => void;
  event: Event;
  /** Refresh the event so the saved settings are reflected. */
  onSaved: () => void;
  /** The wizard's "Choose what guests get" — the RSVP section leads, the rest follows. */
  rsvpFirst?: boolean;
}

/**
 * Do the event's RSVP grant rules apply? Not on a ticketed event — it gives vouchers per TICKET TYPE, and the rules
 * would grant to every attending guest, ticket buyers included. ⚠ ONE gate for the drawer's section, the page's
 * "Generate from rules" item AND its URL stage, so a typed URL cannot run what the menu does not offer.
 */
export const rsvpRulesApply = (event: Pick<Event, "eventMode"> | null | undefined): boolean =>
  !!event && event.eventMode !== EVENT_MODE.TICKETED;

const rowKey = (index: number): string => `rsvp-rule-${index}`;
const placeKey = (index: number): string => `redeem-place-${index}`;

type PlaceRowErrors = Record<number, Partial<Record<"couponTypeId" | "servicePointId", string>>>;

/**
 * ⚠ EVERY NAME HERE IS READ BELOW, or the server's message for it vanishes (`getFormErrors` puts a listed name in
 * `fields` and never in the footer). `settings` lands on the rules editor and the expiry (the blob as a whole);
 * the four `coupon*` names are what `EventCore` keys the places and printed text on (API `COUPON_SETTINGS_FIELD`).
 */
const KNOWN_FORM_FIELDS = [
  "settings",
  COUPON_SETTINGS_FIELD.REDEEM_POINT,
  COUPON_SETTINGS_FIELD.REDEEM_AT,
  COUPON_SETTINGS_FIELD.PRINT_HEADER,
  COUPON_SETTINGS_FIELD.PRINT_FOOTER,
] as const;

const parseSettings = (raw: unknown): EventSettings => {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw || "{}") as EventSettings;
    } catch {
      return {};
    }
  }

  return (raw as EventSettings) ?? {};
};

export const CouponGrantRulesDrawer: React.FC<CouponGrantRulesDrawerProps> = ({ opened, onClose, event, onSaved, rsvpFirst = false }) => {
  const { t } = useTranslation(["coupons", "common"]);

  /** RSVP grant rules apply to an RSVP event only — a ticketed one gives vouchers per ticket type. */
  const isTicketed = !rsvpRulesApply(event);

  const [types, setTypes] = useState<CouponType[]>([]);
  const [points, setPoints] = useState<ServicePoint[]>([]);
  /** ⚠ Not loaded yet is not empty — the pickers show a skeleton until their read answers. */
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [pointsLoaded, setPointsLoaded] = useState(false);
  const [pointsFailed, setPointsFailed] = useState(false);
  const [rows, setRows] = useState<GrantRuleRow[]>([]);
  // Auto-issue on RSVP — default ON (a missing value = on, matching the backend gate).
  const [autoGrant, setAutoGrant] = useState(true);
  /**
   * A4b item 2 — WHEN THIS EVENT'S VOUCHERS STOP BEING WORTH ANYTHING. Per EVENT (owner ruling,
   * 2026-09-24), not per voucher type: a voucher type here is account-wide and may be used by several
   * events, so an expiry on the type would expire it everywhere it is used.
   * ⚠ ENFORCED at redemption (`CouponCore.redeem`), which is the whole point — an expiry that only
   * exists on a printed card sends a guest to queue at a stall that will turn them away.
   */
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [redeemPointId, setRedeemPointId] = useState<string | null>(null);
  const [placeRows, setPlaceRows] = useState<RedeemPlaceRow[]>([]);
  const [printHeader, setPrintHeader] = useState("");
  const [printFooter, setPrintFooter] = useState("");
  // ⚠ ONE stable handler each rather than an inline arrow — the house contract for the shared field family
  // (`FieldChangeHandler`), so a control is not handed a fresh callback on every render.
  const handleExpiryChange = useCallback((_name: string | undefined, value: unknown) => {
    setExpiresAt(value == null ? null : String(value));
  }, []);
  const handleRedeemPointChange = useCallback((_name: string | undefined, value: string | null) => {
    setRedeemPointId(value || null);
  }, []);
  const handleHeaderChange = useCallback((_name: string | undefined, value: string) => {
    setPrintHeader(value ?? "");
  }, []);
  const handleFooterChange = useCallback((_name: string | undefined, value: string) => {
    setPrintFooter(value ?? "");
  }, []);
  const [saving, setSaving] = useState(false);
  // The OpResult of THIS drawer's own save — not the context's, which this never touches.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  /** Presence only — a place row with one of its two choices made. The server judges everything else. */
  const [placeRowErrors, setPlaceRowErrors] = useState<PlaceRowErrors>({});

  // Esc / a click outside / Cancel ask before discarding any change in the drawer (#123).
  const { isDirty, reseed, clear } = useFormSnapshot({
    rows,
    autoGrant,
    expiresAt,
    redeemPointId,
    placeRows,
    printHeader,
    printFooter,
  });

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    setSubmitError(null);
    setPlaceRowErrors({});

    const settings = parseSettings(event.settings);
    const places = couponPlaceSettingsOf(settings);
    const seeded = {
      rows: (settings.coupons?.grantRules ?? []).map((r, i) => ({
        key: rowKey(i),
        couponTypeId: r.couponTypeId,
        quantity: r.quantity,
        grantPer: r.grantPer,
      })),
      autoGrant: settings.coupons?.autoGrant !== false,
      expiresAt: settings.coupons?.expiresAt ?? null,
      redeemPointId: places.redeemServicePointId,
      placeRows: places.redeemAt.map((r, i) => ({ key: placeKey(i), couponTypeId: r.couponTypeId, servicePointId: r.servicePointId })),
      printHeader: places.printHeader ?? "",
      printFooter: places.printFooter ?? "",
    };
    // Refreshed settings keep what the user has changed (#125); another event replaces the form.
    const shown = reseed(seeded, event.id ?? "__new__");

    setRows(shown.rows);
    setAutoGrant(shown.autoGrant);
    setExpiresAt(shown.expiresAt);
    setRedeemPointId(shown.redeemPointId);
    setPlaceRows(shown.placeRows);
    setPrintHeader(shown.printHeader);
    setPrintFooter(shown.printFooter);
  }, [opened, event.settings, event.id, clear, reseed]);

  // The two catalogs the pickers offer — read on open, apart from the seed above: a refresh of the event must not
  // re-read them, and reading them must not re-seed the form.
  useEffect(() => {
    if (!opened || !event.id) {
      return;
    }

    let cancelled = false;

    setTypesLoaded(false);
    setPointsLoaded(false);
    setPointsFailed(false);

    // contextEventId scopes the voucher list to THIS event's account so a cross-account crew member
    // (co_host with tickets.manage) sees the account's vouchers instead of an empty list.
    // ⚠ `listAll` — a picker is a whole set, and a capped one silently leaves vouchers out.
    void CouponTypeService.listAll({ status: [STATUSES.ACTIVE], contextEventId: event.id }).then((result) => {
      if (cancelled) {
        return;
      }

      setTypes(result.code === OP_RESULT_CODES.OK && Array.isArray(result.data) ? (result.data as CouponType[]) : []);
      setTypesLoaded(true);
    });

    void ServicePointService.forEvent(String(event.id)).then((result) => {
      if (cancelled) {
        return;
      }

      const ok = result.code === OP_RESULT_CODES.OK && Array.isArray(result.data);

      setPoints(ok ? (result.data as ServicePoint[]) : []);
      setPointsFailed(!ok);
      setPointsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [opened, event.id]);

  const typeLabel = useCallback(
    (v: CouponType) => v.name || t("coupons:untitledType", { defaultValue: "Voucher" }),
    [t],
  );

  /** The rules offer the account's REUSABLE vouchers — what they have always offered. */
  const ruleTypeOptions = useMemo(
    () => types.filter((v) => v.id && !v.eventId).map((v) => ({ value: v.id as string, label: typeLabel(v) })),
    [types, typeLabel],
  );

  /**
   * A place can be given to any voucher usable HERE: the account's reusable ones and this event's own. ⚠ A stored line
   * whose voucher is gone (removed, disabled) is SHOWN as such — a blank select reads as "not chosen yet".
   */
  const placeTypeOptions = useMemo(() => {
    const options = types
      .filter((v) => v.id && (!v.eventId || v.eventId === event.id))
      .map((v) => ({ value: v.id as string, label: typeLabel(v) }));
    const known = new Set(options.map((o) => o.value));
    const gone = [...new Set(placeRows.map((r) => r.couponTypeId).filter((id) => !!id && !known.has(id)))];

    return [
      ...options,
      ...gone.map((id) => ({ value: id, label: t("coupons:places.voucherGone", { defaultValue: "A voucher no longer available" }) })),
    ];
  }, [types, typeLabel, event.id, placeRows, t]);

  const pointOptions = useMemo(() => {
    const manyVenues = new Set(points.map((p) => p.venueId ?? "event")).size > 1;
    const options = points.filter((p) => p.id).map((p) => ({ value: p.id as string, label: servicePointLabel(p, manyVenues) }));
    const known = new Set(options.map((o) => o.value));
    // ⚠ A stored place the event no longer has (removed, or of a venue it left) is SHOWN as such rather than as a blank
    // select — a blank reads as "not set", and the host would not know the vouchers name a place that is gone.
    const stale = [redeemPointId, ...placeRows.map((r) => r.servicePointId)].filter(
      (id): id is string => !!id && !known.has(id),
    );

    return [
      ...options,
      ...[...new Set(stale)].map((id) => ({
        value: id,
        label: t("coupons:places.gone", { defaultValue: "A place no longer at this event" }),
      })),
    ];
  }, [points, redeemPointId, placeRows, t]);

  const perOptions = useMemo(
    () => [
      { value: "guest", label: t("coupons:rules.perPerson", { defaultValue: "Person" }) },
      { value: "group", label: t("coupons:rules.perGroup", { defaultValue: "Group" }) },
    ],
    [t],
  );

  const handleSubmit = useCallback(async () => {
    if (saving) {
      return;
    }

    // Presence only: a place row with a voucher and no place (or the reverse) is pointed at; an empty one is dropped.
    const presence: PlaceRowErrors = {};

    placeRows.forEach((r, i) => {
      if (r.couponTypeId && !r.servicePointId) {
        presence[i] = { servicePointId: t("coupons:places.needPlace", { defaultValue: "Choose where it is redeemed" }) };
      } else if (!r.couponTypeId && r.servicePointId) {
        presence[i] = { couponTypeId: t("coupons:places.needVoucher", { defaultValue: "Choose the voucher" }) };
      }
    });

    setPlaceRowErrors(presence);

    if (Object.keys(presence).length > 0) {
      return;
    }

    const clean: CouponGrantRule[] = rows
      .filter((r) => r.couponTypeId && r.quantity >= 1)
      .map((r) => ({
        couponTypeId: r.couponTypeId,
        quantity: r.quantity,
        grantPer: r.grantPer as CouponGrantRule["grantPer"],
      }));

    setSaving(true);
    setSubmitError(null);

    // ⚠ The event as it is NOW — `settings` is stored whole, and the copy this page read on open may be behind.
    const fresh = await EventService.get(String(event.id));
    const freshEvent = fresh.code === OP_RESULT_CODES.OK && Array.isArray(fresh.data) ? (fresh.data[0] as Event | undefined) : undefined;
    const current = parseSettings(freshEvent ? freshEvent.settings : event.settings);

    /**
     * Preserve the rest of event.settings — we only own keys of the `coupons` sub-object.
     * ⚠⚠ AND SPREAD `coupons` TOO. This used to REPLACE it wholesale, so any key this drawer did not
     * know about was destroyed by saving a rule.
     * ⚠ A ticketed event's RSVP rules are not on screen, so they are left exactly as stored.
     */
    const settings: EventSettings = {
      ...current,
      coupons: {
        ...(current.coupons ?? {}),
        ...(isTicketed ? {} : { grantRules: clean, autoGrant }),
        // ⚠ Blank → null, never '' — the server treats any falsy value as "no expiry", and an empty
        // string in a settings blob reads as a value somebody meant to set.
        expiresAt: expiresAt || null,
        redeemServicePointId: redeemPointId || null,
        redeemAt: placeRows
          .filter((r) => r.couponTypeId && r.servicePointId)
          .map((r) => ({ couponTypeId: r.couponTypeId, servicePointId: r.servicePointId })),
        printHeader: printHeader.trim() || null,
        printFooter: printFooter.trim() || null,
      },
    };

    const result = await EventService.update({ id: event.id }, { settings });

    setSaving(false);

    // ⚠ A refused save keeps the drawer open with the reason on screen; it never silently closes.
    if (!result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    onSaved();
    onClose();
  }, [saving, placeRows, rows, isTicketed, autoGrant, expiresAt, redeemPointId, printHeader, printFooter, event.id, event.settings, onSaved, onClose, t]);

  useModalShortcuts({ onConfirm: handleSubmit, canConfirm: !saving, enabled: opened });

  // Each refusal lands on the control that edits it; everything else, plus every unnamed error, goes to the footer.
  const { fields: fieldErrors, genericErrors } = useMemo(() => getFormErrors(submitError, KNOWN_FORM_FIELDS), [submitError]);
  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);
  // ⚠ `.some(Boolean)`, never `Object.keys(...).length` — getFormErrors pre-fills every
  // known name with undefined, so a keys check is true even on a clean form.
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean) || Object.keys(placeRowErrors).length > 0;

  const sectionTitle = (text: string) => (
    <Text fw={600} size="lg">
      {text}
    </Text>
  );

  const rsvpSection = isTicketed ? (
    <Stack gap="sm">
      {sectionTitle(t("coupons:settings.issueTitle", { defaultValue: "Who gets vouchers" }))}
      <Text size="md" c="dimmed">
        {t("coupons:settings.ticketedHint", {
          defaultValue:
            "This event sells tickets, so vouchers are given per ticket type — open Tickets and choose Vouchers on a ticket type.",
        })}
      </Text>
    </Stack>
  ) : (
    <Stack gap="md">
      {sectionTitle(t("coupons:settings.rsvpTitle", { defaultValue: "Vouchers guests get when they RSVP" }))}
      <Text size="md" c="dimmed">
        {t("coupons:rules.intro", {
          defaultValue:
            "Vouchers every attending guest gets. With auto-issue on, they're granted the moment a guest RSVPs (or is approved). You can also apply them any time with “Generate from rules” — it only issues what's missing, so it's safe to re-run.",
        })}
      </Text>

      <Switch
        size="md"
        checked={autoGrant}
        onChange={(e) => setAutoGrant(e.currentTarget.checked)}
        disabled={saving}
        label={t("coupons:rules.autoGrant.label", { defaultValue: "Automatically issue on RSVP" })}
        description={t("coupons:rules.autoGrant.description", {
          defaultValue: "Issue these vouchers automatically when a guest RSVPs and is attending. Turn off to issue only manually.",
        })}
      />

      <CouponGrantRulesEditor
        rows={rows}
        onChange={setRows}
        typeOptions={ruleTypeOptions}
        perOptions={perOptions}
        defaultGrantPer="guest"
        loading={!typesLoaded && rows.length > 0}
        skeletonRows={Math.min(rows.length || 2, 4)}
        disabled={saving}
        error={fieldErrors.settings}
        emptyText={t("coupons:rules.empty", { defaultValue: "No rules yet — add one to grant vouchers on RSVP." })}
        hint={t("coupons:rules.hint", {
          defaultValue:
            "Person = one lot per head, multiplied by the RSVP's party size (a +1 is fed too). Group = one lot per RSVP group; a guest with no group counts as a party of one.",
        })}
      />
    </Stack>
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("coupons:settings.title", { defaultValue: "Voucher settings" })}
      position="right"
      size="lg"
      loading={saving}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      errors={formattedGenericError}
      hasFieldErrors={hasFieldErrors}
      isDirty={isDirty}
    >
      <Stack gap="xl">
        {rsvpFirst ? rsvpSection : null}

        <Stack gap="sm">
          {sectionTitle(t("coupons:settings.expiryTitle", { defaultValue: "When they expire" }))}
          {/* ⚠ A Mantine picker, never <input type="date"> — house rule. Clearable, because "no expiry"
              is the normal case and has to be reachable after one has been set. */}
          <DateTimeField
            name="expiresAt"
            dateOnly
            label={t("coupons:rules.expiresAt.label", { defaultValue: "Vouchers expire on" })}
            description={t("coupons:rules.expiresAt.description", {
              defaultValue:
                "After this date every voucher for this event is refused at the stall. Leave empty and they never expire. Applies to all voucher types on this event, RSVP and ticketed.",
            })}
            value={expiresAt}
            onChange={handleExpiryChange}
            disabled={saving}
            error={fieldErrors.settings}
            clearable
          />
        </Stack>

        <Stack gap="sm">
          {sectionTitle(t("coupons:settings.placesTitle", { defaultValue: "Where they are redeemed" }))}
          <Text size="md" c="dimmed">
            {t("coupons:places.intro", {
              defaultValue:
                "One of this event's entrances or desks — printed on the vouchers so guests know where to go. A voucher type can have its own place.",
            })}
          </Text>

          {/* ⚠ Where the controls are not drawn, a refusal about them is still shown — never left in a map nothing renders. */}
          {!(pointsLoaded && !pointsFailed && pointOptions.length > 0) &&
          (fieldErrors[COUPON_SETTINGS_FIELD.REDEEM_POINT] || fieldErrors[COUPON_SETTINGS_FIELD.REDEEM_AT]) ? (
            <Text size="md" c="red">
              {fieldErrors[COUPON_SETTINGS_FIELD.REDEEM_POINT] || fieldErrors[COUPON_SETTINGS_FIELD.REDEEM_AT]}
            </Text>
          ) : null}

          {!pointsLoaded ? (
            <Skeleton height={42} radius="sm" />
          ) : pointsFailed ? (
            // ⚠ A failed read is said out loud, never shown as "no places" — the host would go and create them again.
            <Text size="md" c="red">
              {t("coupons:places.loadFailed", {
                defaultValue: "This event's entrances and desks could not be loaded — close this and open it again to change where vouchers are redeemed.",
              })}
            </Text>
          ) : pointOptions.length === 0 ? (
            <Text size="md" c="dimmed">
              {t("coupons:places.none", {
                defaultValue: "This event has no entrances or desks yet. Add them to its venue or to the event, then choose one here.",
              })}
            </Text>
          ) : (
            <>
              <SelectField
                name="redeemServicePointId"
                label={t("coupons:places.default", { defaultValue: "Redeemed at" })}
                placeholder={t("coupons:places.defaultNone", { defaultValue: "Not said" })}
                data={pointOptions}
                value={redeemPointId}
                onChange={handleRedeemPointChange}
                disabled={saving}
                searchable
                clearable
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                error={fieldErrors[COUPON_SETTINGS_FIELD.REDEEM_POINT]}
              />
              <CouponRedeemPlacesEditor
                rows={placeRows}
                onChange={setPlaceRows}
                typeOptions={placeTypeOptions}
                pointOptions={pointOptions}
                loading={!typesLoaded}
                disabled={saving}
                rowErrors={placeRowErrors}
                error={fieldErrors[COUPON_SETTINGS_FIELD.REDEEM_AT]}
              />
            </>
          )}
        </Stack>

        <Stack gap="sm">
          {sectionTitle(t("coupons:settings.printTitle", { defaultValue: "Printed vouchers" }))}
          <TextField
            name="printHeader"
            label={t("coupons:print.headerLabel", { defaultValue: "Header" })}
            description={t("coupons:print.headerHint", { defaultValue: "A line across the top of every printed voucher." })}
            placeholder={t("coupons:print.headerPlaceholder", { defaultValue: "e.g. Welcome to the Harvest Fair" })}
            value={printHeader}
            onChange={handleHeaderChange}
            maxLength={COUPON_PRINT_TEXT_MAX}
            disabled={saving}
            error={fieldErrors[COUPON_SETTINGS_FIELD.PRINT_HEADER]}
          />
          <TextField
            name="printFooter"
            label={t("coupons:print.footerLabel", { defaultValue: "Footer" })}
            description={t("coupons:print.footerHint", { defaultValue: "A line across the bottom — the small print." })}
            placeholder={t("coupons:print.footerPlaceholder", { defaultValue: "e.g. Not exchangeable for cash" })}
            value={printFooter}
            onChange={handleFooterChange}
            maxLength={COUPON_PRINT_TEXT_MAX}
            disabled={saving}
            error={fieldErrors[COUPON_SETTINGS_FIELD.PRINT_FOOTER]}
          />
        </Stack>

        {rsvpFirst ? null : rsvpSection}
      </Stack>
    </AppDrawer>
  );
};
